import React, { useCallback, useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Loader2, Send, FileText, AlertTriangle, LifeBuoy, ChevronLeft } from "lucide-react";
import { publicApi } from "@/lib/api";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const newSessionId = () => `eits-${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36)}`;

export const SupportChat = ({ compact = false, onRequestEscalation }) => {
  const [config, setConfig] = useState(null);
  const [groups, setGroups] = useState([]);
  const [product, setProduct] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [sessionId] = useState(newSessionId);
  const endRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const [c, p] = await Promise.all([
          publicApi.get("/support/config"),
          publicApi.get("/support/products"),
        ]);
        setConfig(c.data);
        setGroups(p.data.groups || []);
      } catch {
        setConfig({ configured: false, ai_credentials_present: false, support_email: "support@eibattery.com.au" });
      }
    })();
  }, []);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages, busy]);

  const ask = useCallback(async (text) => {
    if (!text.trim() || busy) return;
    setDraft("");
    setMessages((prev) => [...prev, { role: "user", text }]);
    setBusy(true);
    try {
      const response = await fetch(`${API}/support/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: text, product_id: product?.id || null }),
      });
      if (!response.ok || !response.body) {
        const detail = await response.json().catch(() => ({}));
        throw new Error(detail.detail || "The assistant is unavailable right now.");
      }
      setMessages((prev) => [...prev, { role: "assistant", text: "", sources: [] }]);
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop() || "";
        for (const part of parts) {
          const line = part.replace(/^data: /, "").trim();
          if (!line) continue;
          let event;
          try { event = JSON.parse(line); } catch { continue; }
          setMessages((prev) => {
            const next = [...prev];
            const last = { ...next[next.length - 1] };
            if (event.type === "sources") last.sources = event.sources || [];
            if (event.type === "delta") last.text = (last.text || "") + event.text;
            if (event.type === "error") last.error = event.message;
            next[next.length - 1] = last;
            return next;
          });
        }
      }
    } catch (error) {
      setMessages((prev) => [...prev, { role: "assistant", text: "", error: error.message }]);
    } finally {
      setBusy(false);
    }
  }, [busy, product, sessionId]);

  if (!config) {
    return (
      <div className="flex items-center justify-center py-14 text-ei-graphite/60" data-testid="support-loading">
        <Loader2 className="w-5 h-5 animate-spin" />
      </div>
    );
  }

  if (!config.ai_credentials_present) {
    return (
      <div className="p-5 bg-amber-50 border border-amber-200 rounded-md text-sm text-amber-900" data-testid="support-not-configured">
        <strong className="font-display block mb-1">The AI assistant is not configured</strong>
        The AI credentials are missing in this environment, so the assistant cannot answer questions.
        Email <a className="underline" href={`mailto:${config.support_email}`}>{config.support_email}</a> and our technical team will help directly.
      </div>
    );
  }

  const started = messages.length > 0;

  return (
    <div className="flex flex-col h-full min-h-0" data-testid="support-chat">
      <div className={`flex-1 min-h-0 overflow-y-auto ${compact ? "px-4 py-4" : "px-0 py-2"} space-y-4`} data-testid="support-messages">
        <div className="bg-ei-navy/[0.04] border border-ei-mist rounded-md p-4">
          <p className="text-sm text-ei-navy font-medium" data-testid="support-greeting">{config.greeting}</p>
          {product ? (
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-2 bg-ei-navy text-ei-white text-xs font-display font-bold px-3 py-1.5 rounded-sm" data-testid="support-selected-product">
                {product.name}
              </span>
              <button onClick={() => setProduct(null)} className="inline-flex items-center gap-1 text-xs text-ei-graphite/70 hover:text-ei-navy" data-testid="support-change-product">
                <ChevronLeft className="w-3 h-3" /> change product
              </button>
            </div>
          ) : (
            <div className="mt-3 space-y-3" data-testid="support-product-picker">
              {groups.map((group) => (
                <div key={group.kind}>
                  <div className="text-[11px] uppercase tracking-wider text-ei-graphite/50 mb-1.5">{group.kind}</div>
                  <div className="flex flex-wrap gap-2">
                    {group.products.map((item) => (
                      <button
                        key={item.id}
                        onClick={() => setProduct(item)}
                        data-testid={`support-product-${item.id}`}
                        className="text-xs font-medium text-ei-navy bg-white border border-ei-mist hover:border-ei-gold hover:bg-ei-gold/5 px-3 py-2 rounded-sm transition-colors min-h-[36px] text-left"
                      >
                        {item.name}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {product && !started && (
          <div className="flex flex-wrap gap-2" data-testid="support-quick-actions">
            {(config.quick_actions || []).map((action) => (
              <button
                key={action.id}
                data-testid={`support-action-${action.id}`}
                onClick={() => (action.id === "human" ? onRequestEscalation?.(product) : ask(action.prompt))}
                className="inline-flex items-center gap-2 text-xs font-display font-bold text-ei-navy border border-ei-navy/20 hover:border-ei-gold hover:bg-ei-gold/5 px-3 py-2 rounded-sm transition-colors min-h-[38px]"
              >
                {action.id === "human" ? <LifeBuoy className="w-3.5 h-3.5" /> : <FileText className="w-3.5 h-3.5" />}
                {action.label}
              </button>
            ))}
          </div>
        )}

        {messages.map((message, index) => (
          <div key={index} className={message.role === "user" ? "flex justify-end" : ""} data-testid={`support-message-${index}`}>
            <div className={message.role === "user"
              ? "bg-ei-navy text-ei-white text-sm rounded-md px-4 py-2.5 max-w-[85%]"
              : "bg-white border border-ei-mist rounded-md px-4 py-3 w-full"}>
              {message.role === "user" ? (
                message.text
              ) : (
                <>
                  <div className="prose prose-sm max-w-none text-ei-graphite prose-headings:text-ei-navy prose-strong:text-ei-navy prose-a:text-ei-navy">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{message.text || ""}</ReactMarkdown>
                  </div>
                  {message.error && (
                    <p className="text-sm text-red-700 flex items-start gap-2" data-testid="support-error">
                      <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /> {message.error}
                    </p>
                  )}
                  {message.sources?.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-ei-mist" data-testid={`support-sources-${index}`}>
                      <div className="text-[11px] uppercase tracking-wider text-ei-graphite/50 mb-1.5">Supporting documents</div>
                      <ul className="space-y-1">
                        {message.sources.map((source) => (
                          <li key={source.index} className="text-xs">
                            <a href={source.url} target="_blank" rel="noreferrer" className="text-ei-navy hover:text-ei-gold underline underline-offset-2">
                              [{source.index}] {source.title}
                            </a>
                            <span className="text-ei-graphite/60"> · page {source.page}{source.revision_date ? ` · ${source.revision_date}` : ""}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {message.text && !busy && (
                    <button
                      onClick={() => onRequestEscalation?.(product)}
                      className="mt-3 text-xs font-display font-bold text-ei-navy hover:text-ei-gold inline-flex items-center gap-1.5"
                      data-testid={`support-escalate-${index}`}
                    >
                      <LifeBuoy className="w-3.5 h-3.5" /> Send this to a human specialist
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        ))}

        {busy && (
          <div className="flex items-center gap-2 text-sm text-ei-graphite/60" data-testid="support-thinking">
            <Loader2 className="w-4 h-4 animate-spin" /> Checking the approved documents…
          </div>
        )}
        <div ref={endRef} />
      </div>

      <form
        onSubmit={(event) => { event.preventDefault(); ask(draft); }}
        className={`border-t border-ei-mist ${compact ? "p-3" : "pt-4"} flex items-end gap-2 bg-white`}
      >
        <textarea
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter" && !event.shiftKey) { event.preventDefault(); ask(draft); }
          }}
          rows={compact ? 2 : 3}
          placeholder={product ? `Ask about the ${product.name}…` : "Ask a technical question, or pick a product above…"}
          className="flex-1 border border-ei-mist rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-ei-navy resize-none"
          data-testid="support-input"
        />
        <button
          type="submit"
          disabled={busy || !draft.trim()}
          className="inline-flex items-center gap-2 bg-ei-navy text-ei-white font-display text-xs font-bold px-4 py-3 rounded-sm hover:bg-ei-navy/90 disabled:opacity-40 min-h-[44px]"
          data-testid="support-send"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Ask
        </button>
      </form>
      <p className="text-[11px] text-ei-graphite/50 px-1 pt-2 pb-1">
        Answers come only from approved manufacturer documents and always cite their source. Installation, wiring and
        electrical testing must be carried out by your licensed electrical contractor.
      </p>
    </div>
  );
};

export default SupportChat;
